import {createSelector} from 'reselect';
import _ from 'lodash';
import {isNullOrUndefined} from '../../util/utils';
import {financialStatementType} from '../../util/uiconstants';

export const getTrialBalanceSlice = (state) => state.trialBalances;
export const getFinancialStatementSlice = (state) => state.financialStatement;

export const getFinancialStatementByIdSlice = createSelector(
	getFinancialStatementSlice,
	(financialStatementSlice) => financialStatementSlice.byId
);

export const getFinancialStatementIds = createSelector(
	getFinancialStatementSlice,
	(financialStatementSlice) => financialStatementSlice.allIds
);

export const getFinancialStatementById = createSelector(
	getFinancialStatementSlice,
	(financialStatementSlice, financialStatementId) => financialStatementSlice[financialStatementId]
);

export const getSelectedFinancialStatement = createSelector(getFinancialStatementSlice, (financialStatementSlice) => {
	const allIds = financialStatementSlice?.allIds;
	let selectedFinancialStatement;

	for (let i = 0; i < allIds?.length && !selectedFinancialStatement; i++) {
		let financialStatement = financialStatementSlice.byId[allIds[i]];
		if (financialStatement.isSelected) {
			selectedFinancialStatement = financialStatement;
		}
	}

	return selectedFinancialStatement;
});

export const selectFinancialStatementById = (financialStatementId) => {
	return createSelector(getFinancialStatementSlice, (financialStatements) => {
		if (financialStatements && Array.isArray(financialStatements.allIds)) {
			if (financialStatements.allIds.includes(financialStatementId)) {
				return financialStatements.byId[financialStatementId];
			}

			return null;
		}
	});
};

export const getCurrentTrialBalancesByType = (trialBalanceTypeId, stEntityId) => {
	return createSelector([(state) => state.trialBalances], (trialBalances) => {
		if (trialBalances.byId) {
			return Object.values(trialBalances.byId).find((trialBalance) => {
				return (
					trialBalance.trialBalanceTypeId === trialBalanceTypeId &&
					trialBalance.isCurrentVersion &&
					(!stEntityId || trialBalance.stEntityId === stEntityId)
				);
			});
		}
	});
};

export const getCurrentTrialBalanceByType = (trialBalanceTypeId) => (state) => {
	const trialBalance = state.trialBalance;
	if (trialBalance.byId) {
		return Object.values(trialBalance.byId).filter((tb) => {
			return tb.trialBalanceTypeId === trialBalanceTypeId && tb.isCurrentVersion;
		});
	}
};

export const getPriorPeriodComparativeIsVisible = (trialBalanceId, includeArray) => (state) => {
	const trialBalance = state.trialBalance;
	const financialStatements = state.financialStatement;

	const financialStatementIds = trialBalance?.byId[trialBalanceId]?.financialStatement;

	if (financialStatementIds && financialStatementIds.length > 0) {
		let priorPeriodComparativeIsVisible = false;

		for (let i = 0; i < financialStatementIds.length; i++) {
			const index = financialStatementIds[i];
			const financialStatement = financialStatements.byId[index];

			if (
				!isNullOrUndefined(financialStatement) &&
				!isNullOrUndefined(financialStatement?.secondComparativePeriodBalance) &&
				includeArray.includes(financialStatement?.financialStatementTypeId)
			) {
				priorPeriodComparativeIsVisible = true;
			}
		}

		return priorPeriodComparativeIsVisible;
	}
};

export const getFinancialSatementForGrid = (
	includeArray,
	trialBalanceId,
	includeClientData,
	accoundId,
	includeAnnotations
) => (state) => {
	const financialStatement = state.financialStatement;
	const annotations = state.annotations;
	const trialBalance = state.trialBalance;

	const slice = Object.values(financialStatement?.byId).filter((financial) => {
		if (financial.trialBalanceId === trialBalanceId && includeArray.includes(financial.financialStatementTypeId)) {
			return financial;
		}
	});

	const values = [];

	const annotationsSlice = annotations?.byId;

	if (slice) {
		slice.forEach((financial) => {
			if (financial.financialStatementSubTypeId === financialStatementType.AccountType) {
				values.push({
					...financial,
					arc: [financial.financialStatementName]
				});
			}
			if (financial.financialStatementSubTypeId === financialStatementType.AccountSubType) {
				const parentFinancialStatement = slice.find((f) => f.id === financial.parentFinancialStatementId);
				if (parentFinancialStatement) {
					values.push({
						...financial,
						arc: [parentFinancialStatement.financialStatementName, financial.financialStatementName]
					});
				}
			}
			if (financial.financialStatementSubTypeId === financialStatementType.AccountClass) {
				const parentFinancialStatement = slice.find((f) => f.id === financial.parentFinancialStatementId);
				if (parentFinancialStatement) {
					const ancientFinancialStatement = slice.find(
						(f) => f.id === parentFinancialStatement.parentFinancialStatementId
					);
					if (ancientFinancialStatement) {
						values.push({
							...financial,
							arc: [
								ancientFinancialStatement.financialStatementName,
								parentFinancialStatement.financialStatementName,
								financial.financialStatementName
							]
						});
						if (includeAnnotations && financial.annotations.length > 0) {
							const annotation = annotationsSlice[financial.annotations[0]];
							if (annotation) {
								values.push({
									...annotation,
									financialStatementId: financial.id,
									arc: [
										ancientFinancialStatement.financialStatementName,
										parentFinancialStatement.financialStatementName,
										financial.financialStatementName,
										annotation.annotationText
									],
									fullWidthRow: true,
									displayOrder: Number.MIN_VALUE
								});
							}
						}
					}
				}
			}
			if (financial.financialStatementSubTypeId === financialStatementType.AccountSubClass) {
				const parentFinancialStatement = slice.find((f) => f.id === financial.parentFinancialStatementId);
				if (parentFinancialStatement) {
					const ancientFinancialStatement = slice.find(
						(f) => f.id === parentFinancialStatement.parentFinancialStatementId
					);
					if (ancientFinancialStatement) {
						const moreAncientFinancialStatement = slice.find(
							(f) => f.id === ancientFinancialStatement.parentFinancialStatementId
						);
						if (moreAncientFinancialStatement) {
							values.push({
								...financial,
								arc: [
									moreAncientFinancialStatement.financialStatementName,
									ancientFinancialStatement.financialStatementName,
									parentFinancialStatement.financialStatementName,
									financial.financialStatementName
								]
							});
						}
					}
				}
			}

			if (includeClientData && financial.financialStatementSubTypeId === financialStatementType.AccountSubClass) {
				const clientData = trialBalance?.byId[trialBalanceId]?.clientData?.filter((cd) => {
					return (
						cd.accountId === accoundId &&
						cd.financialStatementId === financial.id &&
						cd.financialStatementSubTypeId === financialStatementType.AccountSubClass
					);
				});

				if (clientData && clientData.length > 0) {
					clientData.map((cd) => {
						const parentFinancialStatement = slice.find((f) => f.id === financial.parentFinancialStatementId);
						if (parentFinancialStatement) {
							const ancientFinancialStatement = slice.find(
								(f) => f.id === parentFinancialStatement.parentFinancialStatementId
							);
							if (ancientFinancialStatement) {
								const moreAncientFinancialStatement = slice.find(
									(f) => f.id === ancientFinancialStatement.parentFinancialStatementId
								);
								if (moreAncientFinancialStatement) {
									values.push({
										...cd,
										arc: [
											moreAncientFinancialStatement.financialStatementName,
											ancientFinancialStatement.financialStatementName,
											parentFinancialStatement.financialStatementName,
											financial.financialStatementName
										]
									});
								}
							}
						}
					});
				}
			}
		});
	}

	return _.orderBy(values, ['displayOrder'], ['asc']);
};
